import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ClipboardCheck, Upload, Plus, Trash2, Loader2, Sparkles, FileText, StickyNote, Send, Copy, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  open: { label: "Open", tone: "danger" },
  in_progress: { label: "In Progress", tone: "warning" },
  fixed: { label: "Fixed", tone: "lime" },
  verified: { label: "Verified", tone: "success" },
};
const SEV_TONE = { low: "outline", medium: "warning", high: "danger", critical: "danger" };
const SEVERITIES = ["low", "medium", "high", "critical"];

function Stat({ label, value, tone, testid }) {
  return (
    <div className="card-flat p-3" data-testid={testid}>
      <div className="label-eyebrow text-[9px] mb-1">{label}</div>
      <div className={`font-mono text-xl font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function QaFindings() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", module: "" });
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [memoOpen, setMemoOpen] = useState(false);
  const [memoMd, setMemoMd] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/qa-tracker").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/qa-tracker/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const upd = r.data.updated ? `, updated ${r.data.updated} existing` : "";
      toast.success(`Extracted ${r.data.findings.length} new finding(s)${upd} from ${file.name}`);
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const addManual = async () => {
    try {
      await api.post("/qa-tracker", { ...form, description: form.description || null, module: form.module || null });
      toast.success("Finding added"); setAddOpen(false);
      setForm({ title: "", description: "", severity: "medium", module: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setStatus = async (f, status) => {
    try { await api.patch(`/qa-tracker/${f.id}`, { status }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete finding ${f.code}?`)) return;
    try { await api.delete(`/qa-tracker/${f.id}`); toast.success("Finding deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const saveNote = async () => {
    try {
      await api.patch(`/qa-tracker/${noteFor.id}`, { resolution_note: noteText });
      toast.success("Fix note saved"); setNoteFor(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openMemo = async () => {
    try {
      const r = await api.get("/qa-tracker/reply-memo");
      setMemoMd(r.data.markdown); setMemoOpen(true);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const copyMemo = async () => {
    try { await navigator.clipboard.writeText(memoMd); toast.success("Markdown copied to clipboard"); }
    catch { toast.error("Copy failed — select the text manually"); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get("/qa-tracker/reply-memo.pdf", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = "qa_reply_memo.pdf"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const visible = useMemo(() => {
    const items = data?.findings || [];
    return filter === "all" ? items : items.filter((f) => f.status === filter);
  }, [data, filter]);

  if (!data) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading QA findings…</div>;

  const total = data.findings.length;
  const done = (data.counts.fixed || 0) + (data.counts.verified || 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const canEdit = data.can_edit;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="qa-findings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Data Management</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><ClipboardCheck className="h-8 w-8 text-accent" /> QA Findings Tracker</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Upload a QA/UAT memo and AI turns it into a living defect checklist — track each finding from Open through Fixed to Verified.</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".docx,.pdf" className="hidden" onChange={onUpload} data-testid="qa-upload-input" />
            <Button variant="primary" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="qa-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Extracting…" : "Upload QA memo"}
            </Button>
            <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="qa-add-btn"><Plus className="h-4 w-4" /> Add finding</Button>
            <Button variant="outline" onClick={openMemo} data-testid="qa-reply-memo-btn"><Send className="h-4 w-4" /> Reply memo</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total findings" value={total} testid="qa-stat-total" />
        <Stat label="Open" value={data.counts.open} tone={data.counts.open ? "text-red-400" : ""} testid="qa-stat-open" />
        <Stat label="In progress" value={data.counts.in_progress} tone={data.counts.in_progress ? "text-[#FFB800]" : ""} testid="qa-stat-inprogress" />
        <Stat label="Fixed" value={data.counts.fixed} tone="text-[#00E65B]" testid="qa-stat-fixed" />
        <Stat label="Verified" value={data.counts.verified} tone="text-[#00E65B]" testid="qa-stat-verified" />
        <div className="card-flat p-3" data-testid="qa-stat-progress">
          <div className="label-eyebrow text-[9px] mb-1">Resolution progress</div>
          <div className="font-mono text-xl font-bold">{pct}%</div>
          <Progress value={pct} className="h-1.5 mt-1" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="qa-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {data.memos.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            {data.memos.slice(0, 4).map((m) => (
              <Badge key={m.id} variant={m.status === "extracted" ? "outline" : "danger"} className="text-[9px]" data-testid="qa-memo-chip">
                {m.filename} · {m.findings_count}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Finding</TableHead><TableHead>Module</TableHead>
              <TableHead>Severity</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow><TableCell colSpan={canEdit ? 7 : 6} className="text-center text-muted-foreground py-10 text-sm">
                {total === 0 ? "No findings yet — upload a QA memo or add one manually." : "No findings match this filter."}
              </TableCell></TableRow>
            )}
            {visible.map((f) => (
              <TableRow key={f.id} data-testid="qa-finding-row">
                <TableCell className="font-mono text-xs">{f.code}</TableCell>
                <TableCell className="max-w-[360px]">
                  <div className="text-sm font-medium">{f.title}</div>
                  {f.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2" title={f.description}>{f.description}</div>}
                  {f.resolution_note && <div className="text-[10px] text-accent/80 mt-0.5" data-testid={`qa-fix-note-${f.code}`}>Fix note: {f.resolution_note}</div>}
                </TableCell>
                <TableCell className="text-xs">{f.module ? <Badge variant="outline" className="text-[9px]">{f.module}</Badge> : "—"}</TableCell>
                <TableCell><Badge variant={SEV_TONE[f.severity] || "outline"} className="text-[9px] uppercase">{f.severity}</Badge></TableCell>
                <TableCell className="text-[10px] text-muted-foreground max-w-[140px]">
                  {f.source === "ai_extract" ? <span className="inline-flex items-center gap-1 truncate" title={f.memo_filename}><Sparkles className="h-3 w-3 text-accent shrink-0" /><span className="truncate">{f.memo_filename}</span></span> : "Manual"}
                </TableCell>
                <TableCell>
                  {canEdit ? (
                    <Select value={f.status} onValueChange={(v) => setStatus(f, v)}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid={`qa-status-select-${f.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={STATUS_META[f.status]?.tone || "outline"} className="text-[9px]" data-testid={`qa-status-badge-${f.code}`}>{STATUS_META[f.status]?.label || f.status}</Badge>
                  )}
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit dev fix note (appears in the QA reply memo)"
                        onClick={() => { setNoteFor(f); setNoteText(f.resolution_note || ""); }} data-testid={`qa-note-btn-${f.code}`}>
                        <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(f)} data-testid="qa-delete-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="qa-add-dialog">
          <DialogHeader><DialogTitle className="font-display">Add QA finding</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="qa-add-title" /></div>
            <div><Label>Description / desired outcome</Label><Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="qa-add-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm((p) => ({ ...p, severity: v }))}>
                  <SelectTrigger data-testid="qa-add-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Module (optional)</Label><Input value={form.module} onChange={(e) => setForm((p) => ({ ...p, module: e.target.value }))} placeholder="e.g. Import" data-testid="qa-add-module" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button variant="primary" disabled={!form.title.trim()} onClick={addManual} data-testid="qa-add-save-btn">Add finding</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fix note dialog */}
      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent className="max-w-md" data-testid="qa-note-dialog">
          <DialogHeader><DialogTitle className="font-display">Dev fix note — {noteFor?.code}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{noteFor?.title}</p>
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4}
            className="w-full rounded-md border border-border bg-surface p-2 text-sm"
            placeholder="What was fixed, where, and how QA can verify it…" data-testid="qa-note-textarea" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setNoteFor(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveNote} data-testid="qa-note-save-btn">Save note</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reply memo dialog */}
      <Dialog open={memoOpen} onOpenChange={setMemoOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="qa-memo-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Send className="h-4 w-4 text-accent" /> Dev → QA Reply Memo</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyMemo} data-testid="qa-memo-copy-btn"><Copy className="h-3.5 w-3.5" /> Copy markdown</Button>
            <Button variant="primary" size="sm" onClick={downloadPdf} data-testid="qa-memo-pdf-btn"><Download className="h-3.5 w-3.5" /> Download PDF</Button>
          </div>
          <pre className="text-[11px] leading-relaxed bg-surface border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap" data-testid="qa-memo-markdown">{memoMd}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
